const parseDate = (value: string | undefined): Date | null => {
	if (!value) {
		return null;
	}

	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const determinePhase = (
	checkInDate: string | undefined,
	checkOutDate: string | undefined
): string => {
	const now = new Date();
	const checkin = parseDate(checkInDate);
	const checkout = parseDate(checkOutDate);

	if (!checkin || !checkout) {
		return 'Unknown';
	}

	if (now < checkin) {
		return 'Pre-Stay';
	}

	if (now >= checkin && now < checkout) {
		return 'Stay';
	}

	return 'Post-Stay';
};

type BookingSummary = {
	checkInDate?: string;
	checkOutDate?: string;
	confirmationNumber?: string;
	roomType?: {
		name?: string;
	};
	propertyOwsCode?: string;
	propertyName?: string;
};

type PropertyAnalytics = {
	enRegion?: string;
	enCountry?: string;
	orsCode?: string;
};

type Profile = {
	goldenId?: string;
};

type ItineraryData = {
	hasRequestedArrivalTransportation?: boolean;
	hasRequestedDepartureTransportation?: boolean;
	timelineItems?: unknown[];
};

type SessionData = {
	sessionStartTime?: number;
	visitedPages?: string[];
	searchTerm?: string;
};

type EmbeddedDataParams = {
	bookingSummary?: BookingSummary;
	propertyAnalytics?: PropertyAnalytics;
	profile?: Profile;
	urlPath?: string;
	itineraryData?: ItineraryData;
	userHasItinerary?: boolean;
	sessionData?: SessionData;
	interceptId?: string;
	environmentTag?: string;
};

export function getItineraryQualtricsEmbeddedData({
	bookingSummary,
	propertyAnalytics,
	profile,
	urlPath = '',
	userHasItinerary = false,
	sessionData = {},
	interceptId = '',
	environmentTag = 'production',
}: EmbeddedDataParams) {
	const isBrowser = typeof window !== 'undefined';
	const currentUrl = isBrowser ? window.location.href : urlPath;
	const referrer = typeof document !== 'undefined' ? document.referrer : '';
	const qsiEmbeddedData = isBrowser ? (window as any).QSI?.EmbeddedData : undefined;

	const rawQualtricsEmbeddedData = {
		Intercept_Time_Spent_on_Site: qsiEmbeddedData?.getTimeOnSite?.(),
		Intercept_Total_Visited_Page_Count: qsiEmbeddedData?.getTotalPageCount?.(),
		Intercept_Total_Unique_Visited_Page_Count: qsiEmbeddedData?.getPageCount?.(),
		Intercept_Recorded_Site_History: qsiEmbeddedData?.getHistory?.(),
		VisitorUUID: isBrowser ? (window as any).utag_data?.tealium_session_id : undefined,
	};

	const qualtricsEmbeddedData = Object.entries(rawQualtricsEmbeddedData).reduce(
		(acc, [key, value]) => {
			if (value) {
				acc[key] = value;
			}

			return acc;
		},
		{} as Record<string, unknown>
	);

	const visitedPages = sessionData.visitedPages || [];
	const uniquePages = new Set(visitedPages).size;

	const embeddedData = {
		date: new Date().toISOString(),
		Screen: urlPath || (isBrowser ? window.location.pathname : ''),
		Page: urlPath || (isBrowser ? window.location.pathname : ''),
		Channel: 'Web',
		Environment: environmentTag,

		//Intercept metadata fields
		Intercept_Search_Term: sessionData.searchTerm || '',
		Intercept_Site_Referrer: referrer,
		Intercept_Page_Referrer: referrer,
		Intercept_Current_Page_URL: currentUrl,
		Intercept_ID: interceptId || '',
		Intercept_Creative_ID: 'itinerary_overlay_v1',

		//Phase determination (Pre-Stay for upcoming trips, Stay for within trip)
		Phase:
			bookingSummary?.checkInDate && bookingSummary?.checkOutDate
				? determinePhase(bookingSummary.checkInDate, bookingSummary.checkOutDate)
				: 'Unknown',

		//Property data (PropertyCode fields)
		PropertyCode: bookingSummary?.propertyOwsCode || '',
		TYPE: propertyAnalytics?.enRegion || '',
		REGION: propertyAnalytics?.enCountry || '',
		CODE: propertyAnalytics?.orsCode || '',
		'Property Full Name': bookingSummary?.propertyName || '',

		//Journey phase fields
		JourneyPhase: '', // To be populated by tracking system
		Category: '', // To be populated by tracking system
		FullJourneyPhase: '', // To be populated by tracking system

		//Screen visited counts
		'Unique Screen Visited Count': uniquePages,
		'Total Screen Visited Count': visitedPages.length,

		//User data
		plural: userHasItinerary ? 'plural' : 'singular', // Indicates multiple items/trips
		GoldenID: profile?.goldenId || 'anonymous',

		//Booking specific data
		CheckInDate: bookingSummary?.checkInDate || '',
		CheckOutDate: bookingSummary?.checkOutDate || '',
	};

	return {
		...embeddedData,
		...qualtricsEmbeddedData,
	};
}
